import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from "react-bootstrap";
import { Input } from "reactstrap";
import SimpleHeader from "components/Headers/SimpleHeader";
import { sipProfileForm } from "../../configs/Forms/AdvancedForms";
import { getSipProfile, insertSipProfile, updateSipProfile } from "./sipService";

const titleCase = (s) =>
  s.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

const SipAddEdit = () => {
  const { sip_profile_uuid } = useParams();
  const navigate = useNavigate();
  const editing = Boolean(sip_profile_uuid);

  // Initialize state with defaults for new profiles
  const [sipProfile, setSipProfile] = useState(() => {
    if (editing) return {};
    const defaults = {};
    sipProfileForm.sections["Basic Info"].forEach(field => {
      defaults[field] = sipProfileForm.defaults?.[field] ?? "";
    });
    return defaults;
  });

  const [domains, setDomains] = useState([]);
  const [settings, setSettings] = useState([]);

  const [loading, setLoading] = useState(editing);
  const [error, setError] = useState("");

  const setterMap = { Domains: setDomains, Settings: setSettings };
  const arrayMap = { Domains: domains, Settings: settings };

  // Fetch SIP profile if editing
  useEffect(() => {
    if (!editing) return;

    const fetchProfile = async () => {
      try {
        setLoading(true);
        const data = await getSipProfile(sip_profile_uuid);
        if (data.error) {
          setError(data.error);
          return;
        }

        const result = data.result || {};
        setSipProfile(result);
        setDomains(result.domains?.length ? result.domains : domains);
        setSettings(result.settings?.length ? result.settings : settings);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [sip_profile_uuid, editing]);

  // Helpers
  const handleChange = (field, value) =>
    setSipProfile(prev => ({ ...prev, [field]: value }));

  const handleArrayChange = (setter, index, field, value, array) => {
    const updated = [...array];
    updated[index] = { ...updated[index], [field]: value };
    setter(updated);
  };

  const handleAddItem = (setter, sectionName, array) => {
    const template = {};
    sipProfileForm.sections[sectionName].forEach(field => {
      template[field] = sipProfileForm.defaults?.[field] ?? "";
    });
    setter([...array, template]);
  };

  const handleDeleteItem = (section, index) => {
    const setter = setterMap[section];
    const array = arrayMap[section];
    setter(array.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setError("");
    const missing = [];

    // Validate main required fields
    sipProfileForm.required.forEach(field => {
      if (!sipProfile[field]) missing.push(field);
    });

    // Validate required sections
    Object.entries(sipProfileForm.requiredSections || {}).forEach(([section, fields]) => {
      const items = { Domains: domains, Settings: settings }[section] || [];
      items.forEach((item, idx) => {
        fields.forEach(f => {
          if (!item[f]) missing.push(`${section}[${idx}].${f}`);
        });
      });
    });

    if (missing.length) {
      setError(`Required fields missing: ${missing.join(", ")}`);
      return;
    }

    try {
      // Create a copy of the main profile without empty fields
      const sanitizedProfile = {};
      Object.entries(sipProfile).forEach(([key, value]) => {
        if (value !== undefined && value !== "" && value !== null) {
          sanitizedProfile[key] = value;
        }
      });

      // Similarly sanitize Domains and Settings arrays
      const sanitizeArray = (arr) =>
        arr.map(item => {
          const sanitized = {};
          Object.entries(item).forEach(([key, value]) => {
            if (value !== undefined && value !== "" && value !== null) sanitized[key] = value;
          });
          return sanitized;
        }).filter(item => Object.keys(item).length > 0); // remove empty objects

      const payload = {
        sipProfile: sanitizedProfile,
        domains: sanitizeArray(domains),
        settings: sanitizeArray(settings),
      };

      if (editing) {
        await updateSipProfile(payload.sipProfile, payload.domains, payload.settings);
      } else {
        await insertSipProfile(payload.sipProfile, payload.domains, payload.settings);
      }

      navigate("/admin/hucs/sipProfiles");
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to save");
    }
  };

  const renderField = (section, field, value, onChange) => {
    const type = sipProfileForm.types[field];
    const options = sipProfileForm.options?.[field] || [];
    const defaultValue = sipProfileForm.defaults?.[field];
    const effectiveValue = value ?? defaultValue ?? "";
    const label =
      sipProfileForm.labels?.[field] || titleCase(field);

    const placeholder = value
      ? ""
      : `Enter ${label.toLowerCase()}`;

    // BOOLEAN
    if (type === "boolean") {
      return (
        <Input
          type="select"
          value={effectiveValue}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="true">True</option>
          <option value="false">False</option>
        </Input>
      );
    }

    // SELECT
    if (type === "select") {
      return (
        <Input
          type="select"
          value={effectiveValue}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">
            Select {sipProfileForm.labels[field]}
          </option>

          {options.map((opt) =>
            typeof opt === "object" ? (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ) : (
              <option key={opt} value={opt}>
                {opt}
              </option>
            )
          )}
        </Input>
      );
    }

    // DEFAULT (text / number)
    return (
      <Input
        type="text"
        value={effectiveValue}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  };

  if (loading)
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
        <p className="mt-2">Loading SIP Profile...</p>
      </div>
    );

  return (
    <>
      <SimpleHeader
        name={`${editing ? "Edit" : "Add"} SIP Profile`}
        parentName="SIP Profiles"
      />
      <Container className="mt--6">
        <Card className="shadow-sm" style={{ marginTop: "-3rem" }}>
          <Card.Header className="header-theme">
            <h4 className="mb-0">{editing ? "EDIT SIP PROFILE" : "ADD SIP PROFILE"}</h4>
          </Card.Header>
          <Card.Body className="p-4">
            {error && <Alert variant="danger">{error}</Alert>}
            <Form>
              {Object.entries(sipProfileForm.sections).map(([sectionName, fields]) => {
                const setter = setterMap[sectionName];
                const items = arrayMap[sectionName];

                return (
                  <div key={sectionName}>
                    <h5 className="section-title mb-3 mt-4" style={{ fontSize: "1.5rem", fontWeight: 700 }}>
                      {sectionName}
                    </h5>

                    {["Domains", "Settings"].includes(sectionName) &&
                      items?.map((item, idx) => (
                        <Row key={idx} className="mb-2">
                          {fields.map((field, i) => (
                            <Col md={3} key={i}>
                              <Form.Group className="mb-3">
                                <Form.Label>
                                  {sipProfileForm.labels[field] || field}
                                  {(sipProfileForm.requiredSections?.[sectionName] || []).includes(field) && (
                                    <span style={{ color: "red" }}> *</span>
                                  )}
                                </Form.Label>
                                {renderField(
                                  sectionName,
                                  field,
                                  item[field] ?? "",
                                  (value) => handleArrayChange(setter, idx, field, value, items)
                                )}
                              </Form.Group>
                            </Col>
                          ))}
                          <Col md={2}>
                            <Button variant="danger" onClick={() => handleDeleteItem(sectionName, idx)}>Delete</Button>
                          </Col>
                        </Row>
                      ))}

                    {["Domains", "Settings"].includes(sectionName) && (
                      <Button className="mt-2" onClick={() => handleAddItem(setter, sectionName, items)}>
                        Add {sectionName.slice(0, -1)}
                      </Button>
                    )}

                    {sectionName === "Basic Info" && (
                      <Row className="mb-2">
                        {fields.map((field, i) => (
                          <Col md={4} key={i}>
                            <Form.Group className="mb-3">
                              <Form.Label>
                                {sipProfileForm.labels[field] || field}
                                {sipProfileForm.required.includes(field) && <span style={{ color: "red" }}> *</span>}
                              </Form.Label>
                              {renderField(
                                sectionName,
                                field,
                                sipProfile[field] ?? "",
                                (value) => handleChange(field, value)
                              )}
                            </Form.Group>
                          </Col>
                        ))}
                      </Row>
                    )}
                  </div>
                );
              })}

              <div className="mt-4 d-flex justify-content-between">
                <Button variant="danger" onClick={() => navigate("/admin/hucs/sip-profiles")}>
                  Cancel
                </Button>
                <Button variant="success" onClick={handleSave}>Save</Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default SipAddEdit;
